import type { ReactNode } from 'react'
import { BODY, INK, linearCss, sparkle } from './shared'

/**
 * What a Sprout Buddy can be made of (Sam, 8 and 9 Oct 2026).
 *
 * A plain colour is just two colours: arms and legs, and the shading down
 * the right side. A special one also has a body gradient (`stops`), a swatch
 * for the buddy maker (`css`), and up to four drawing layers:
 *
 *   defs     gradients and patterns it needs
 *   behind   before the legs and body (a glow, a cape of light, a puddle)
 *   finish   on the body, clipped to its outline (facets, grain, cracks)
 *   front    over the body's edge (drips, icicles, the near half of a ring)
 *
 * Each layer is a function of `uid`, the drawing's own id, because a screen
 * often shows many buddies at once and gradient ids must not collide.
 *
 * What makes each one read as what it is, at 46 pixels:
 *
 *   gem      the cut: a mesh of triangles in the stone's own tones, with
 *            crisp white edges, as a cut stone splits light
 *   metal    hard bands of light and dark (a mirror shows the sky and the
 *            ground), a bright streak, a dark rim, a sharp glint
 *   wood     grain lines and a knot
 *   slime    drips hanging off the bottom, a puddle, bubbles, a wet shine
 *   ice      icicles, frost lines and a cold shine
 *   lava     dark rock with glowing cracks
 *   fire     flames licking off every edge, flickering
 *   root     twisted bark, moss, leaves at the shoulders, roots at the feet
 *   space    a dark body with light in it: stars, clouds of colour, a burst
 *   portal   a ring of orange sparks running round, the other place inside
 */
export interface Material {
  limbs: string
  shade: string
  stops?: [number, string][]
  radial?: boolean
  /** How far right the bottom of a straight gradient leans (0 = straight down). */
  tilt?: number
  css?: string
  /** Face colour, when the body is too dark for the usual brown. */
  face?: string
  defs?: (uid: string) => ReactNode
  behind?: (uid: string) => ReactNode
  finish?: (uid: string) => ReactNode
  front?: (uid: string) => ReactNode
}

// ------------------------------------------------------------------ gems

type Tone = 'w' | 'i' | 'b' | 's' | 'l' | 'm' | 'p'
type GemTones = Record<Tone, readonly [string, number]>

// The cut: rows of triangles from the point down, wider than the body so the
// clip trims the outside edge. Light comes from the top left, so those
// facets get the lightest tones and the lower right the deepest. `l`, `m`
// and `p` are the stone's "fire", kept to a few facets.
const P = {
  A: '60,32', l0: '16,58', r0: '104,58',
  L1: '40,64', C1: '60,70', R1: '80,64',
  L2: '18,94', M2a: '40,90', C2: '60,98', M2b: '80,90', R2: '102,94',
  L3: '18,124', M3a: '42,118', C3: '60,126', M3b: '78,118', R3: '102,124',
  B1: '28,144', B2: '60,144', B3: '92,144',
}
type Pt = keyof typeof P
const tri = (a: Pt, b: Pt, c: Pt, tone: Tone): [string, Tone] => [`${P[a]} ${P[b]} ${P[c]}`, tone]
const CUT: [string, Tone][] = [
  tri('A', 'l0', 'L1', 'w'), tri('A', 'L1', 'C1', 'i'), tri('A', 'C1', 'R1', 'w'), tri('A', 'R1', 'r0', 'l'),
  tri('l0', 'L2', 'L1', 'i'), tri('L1', 'L2', 'M2a', 'w'), tri('L1', 'M2a', 'C1', 'm'), tri('C1', 'M2a', 'C2', 'i'),
  tri('C1', 'C2', 'M2b', 'b'), tri('C1', 'M2b', 'R1', 'p'), tri('R1', 'M2b', 'R2', 'b'), tri('r0', 'R1', 'R2', 'i'),
  tri('L2', 'L3', 'M3a', 'l'), tri('L2', 'M3a', 'M2a', 'i'), tri('M2a', 'M3a', 'C2', 'w'), tri('C2', 'M3a', 'C3', 'b'),
  tri('C2', 'C3', 'M3b', 'i'), tri('C2', 'M3b', 'M2b', 'm'), tri('M2b', 'M3b', 'R2', 's'), tri('R2', 'M3b', 'R3', 'b'),
  tri('L3', 'B1', 'M3a', 'm'), tri('M3a', 'B1', 'B2', 'b'), tri('M3a', 'B2', 'C3', 'p'), tri('C3', 'B2', 'M3b', 's'),
  tri('M3b', 'B2', 'B3', 'b'), tri('M3b', 'B3', 'R3', 's'),
]

function cut(tones: GemTones): ReactNode {
  return (
    <g>
      {CUT.map(([points, tone], i) => (
        <polygon key={i} points={points} fill={tones[tone][0]} opacity={tones[tone][1]}
          stroke="#FFFFFF" strokeWidth={0.9} strokeLinejoin="round" />
      ))}
      {/* the edge where the light catches, top left */}
      <path d="M58 40 L40 64 L20 94" stroke="#FFFFFF" strokeWidth={2} fill="none" strokeLinecap="round" opacity={0.9} />
      {sparkle(40, 64, 4.5, 'd')}{sparkle(80, 90, 3.5, 'e')}{sparkle(60, 126, 3, 'f')}
    </g>
  )
}

/** The swatch: a cone of hard-edged slices, which reads as facets even small. */
const gemCss = (cols: string[]) =>
  `conic-gradient(from 15deg at 50% 45%,${cols.map((c, i) => `${c} 0 ${((i + 1) * 100) / cols.length}%`).join(',')})`

function gem(limbs: string, shade: string, stops: [number, string][], tones: GemTones, face?: string): Material {
  return {
    limbs, shade, stops, tilt: 1, face,
    css: gemCss([tones.w[0], tones.i[0], tones.l[0], tones.b[0], tones.w[0], tones.m[0], tones.s[0], tones.i[0]]),
    finish: () => cut(tones),
  }
}

// ---------------------------------------------------------------- metals

function metal(limbs: string, shade: string, stops: [number, string][], brushed = false): Material {
  return {
    limbs, shade, stops, tilt: 0.35, css: linearCss(165, stops),
    defs: brushed ? (uid) => (
      <pattern id={`t${uid}`} width="40" height="3" patternUnits="userSpaceOnUse">
        <rect width="40" height="1" fill="#FFFFFF" opacity="0.16" />
        <rect y="1.5" width="40" height="0.8" fill="#000000" opacity="0.08" />
      </pattern>
    ) : undefined,
    finish: (uid) => (
      <g>
        {brushed ? <rect x={20} y={30} width={80} height={112} fill={`url(#t${uid})`} /> : null}
        {/* darker at the edge, so it looks round and hard */}
        <path d={BODY} fill="none" stroke="#000000" strokeWidth={10} opacity={0.2} />
        {/* the bright streak down the left, and a thin rim light on the right */}
        <path d="M52 48 C42 62 33 80 32 100" stroke="#FFFFFF" strokeWidth={5} fill="none" strokeLinecap="round" opacity={0.75} />
        <path d="M89 86 C92 98 91 112 85 124" stroke="#FFFFFF" strokeWidth={2} fill="none" strokeLinecap="round" opacity={0.5} />
        <ellipse cx={47} cy={60} rx={3.5} ry={6} transform="rotate(30 47 60)" fill="#FFFFFF" opacity={0.9} />
        {sparkle(46, 58, 6, 'g1')}{sparkle(82, 120, 3.5, 'g2')}
      </g>
    ),
  }
}

// ------------------------------------------------------------ the rest

// The black hole's ring is two halves: the far one drawn before the body,
// the near one after, so it looks as if it goes round the buddy.
const RING_BACK = 'M-47 0 A47 9 0 0 1 47 0'
const RING_FRONT = 'M-47 0 A47 9 0 0 0 47 0'

const WOOD_GRAIN = [30, 38, 46, 54, 62, 70, 78, 86, 94].map((x, i) =>
  `M${x} 28 C${x - 4 + (i % 3)} 58 ${x + 5} 88 ${x + (i % 2 ? 2 : -2)} 118 S ${x + 3} 140 ${x} 152`)

const NEBULA_CLOUDS: [string, number, number, number, number, number][] = [
  // [gradient, cx, cy, rx, ry, angle]
  ['n1', 44, 68, 28, 18, -25], ['n2', 80, 104, 24, 16, 25], ['n3', 56, 124, 32, 14, 0],
  ['n4', 72, 58, 14, 10, 10], ['n1', 32, 108, 12, 10, 0], ['n2', 50, 92, 10, 7, 0],
]

const burst = (() => {
  const points: string[] = []
  for (let i = 0; i < 32; i++) {
    const a = (i * Math.PI) / 16
    const r = i % 2 ? 50 : i % 4 ? 64 : 72
    points.push(`${(60 + r * Math.cos(a)).toFixed(1)},${(94 + r * Math.sin(a)).toFixed(1)}`)
  }
  return points.join(' ')
})()

const LAVA_CRACKS = [
  'M60 34 L56 50 L63 60 L57 74',
  'M22 90 L32 86 L36 76 L30 66',
  'M98 88 L88 84 L84 72 L90 62',
  'M24 118 L34 122 L38 132',
  'M96 116 L86 120 L84 132',
  'M44 140 L48 126 L58 122 L66 128 L74 120 L78 140',
  'M57 74 L50 80 M57 74 L66 79',
]

// A flame, base at 0,0 and tip straight up, w wide and h tall. Drawn as a
// path with the size already in it, so the flicker animation (a CSS
// transform) has nothing to fight with.
function flame(w: number, h: number): string {
  const p = (x: number, y: number) => `${(x * w).toFixed(1)} ${(y * h).toFixed(1)}`
  return `M0 0 C${p(0.55, 0)} ${p(0.62, -0.35)} ${p(0.42, -0.6)} C${p(0.3, -0.76)} ${p(0.1, -0.84)} ${p(0, -1)} `
    + `C${p(-0.06, -0.8)} ${p(-0.28, -0.72)} ${p(-0.42, -0.55)} C${p(-0.62, -0.3)} ${p(-0.52, 0)} 0 0 Z`
}

// [x, y, width, height, lean]: flames on the body's edge, leaning outwards,
// drawn behind it so only the part outside shows.
const FLAMES: [number, number, number, number, number][] = [
  [28, 86, 20, 44, -28], [92, 86, 20, 44, 28],
  [40, 60, 14, 34, -14], [80, 60, 14, 34, 14],
  [30, 118, 14, 28, -42], [90, 118, 14, 28, 42],
]

// Bark that twists, where wood's grain runs straight.
const BARK = [28, 36, 44, 52, 60, 68, 76, 84, 92].map((x, i) =>
  `M${x} 30 C${x + 6} 58 ${x - 6} 78 ${x + (i % 2 ? 4 : 2)} 100 S ${x - 4} 128 ${x + 2} 150`)

// Roots reaching out from the bottom of the body to the ground.
const ROOTS = [
  'M36 128 C30 136 24 140 12 145', 'M24 140 C22 145 19 148 15 152',
  'M84 128 C90 136 96 140 108 145', 'M96 140 C98 145 101 148 105 152',
]

const CAMO: [string, string][] = [
  ['#3F4A22', 'M28 66 C36 58 50 62 48 74 C46 84 32 84 28 76 Z'],
  ['#3F4A22', 'M70 52 C80 50 88 60 82 66 C74 72 64 62 70 52 Z'],
  ['#3F4A22', 'M78 108 C88 102 98 112 94 122 C88 130 74 124 78 108 Z'],
  ['#3F4A22', 'M34 116 C42 112 52 120 46 130 C40 136 30 126 34 116 Z'],
  ['#A8955E', 'M52 40 C60 38 66 46 60 54 C54 56 48 48 52 40 Z'],
  ['#A8955E', 'M86 78 C94 76 98 88 92 94 C86 96 80 86 86 78 Z'],
  ['#A8955E', 'M22 90 C30 86 36 96 30 104 C24 106 18 98 22 90 Z'],
  ['#A8955E', 'M52 126 C60 122 70 130 64 138 C58 142 48 132 52 126 Z'],
  ['#2E2A1E', 'M40 50 C44 46 52 50 48 58 C44 60 38 56 40 50 Z'],
  ['#2E2A1E', 'M86 98 C90 96 94 102 90 106 C86 108 83 102 86 98 Z'],
  ['#2E2A1E', 'M64 132 C68 128 74 132 72 138 C68 140 62 136 64 132 Z'],
]

export const MATERIALS = {
  // ---- everyday
  tangerine: { limbs: '#F57C1F', shade: '#D85E10' },
  peach: { limbs: '#F9A66C', shade: '#E0834A' },
  berry: { limbs: '#E2557A', shade: '#C23A60' },
  sunny: { limbs: '#F6C343', shade: '#DCA21E' },
  sky: { limbs: '#5AB4E5', shade: '#3893C9' },
  mint: { limbs: '#4CC3A0', shade: '#2FA382' },
  grape: { limbs: '#9B7BE0', shade: '#7A5AC4' },
  cocoa: { limbs: '#B07A50', shade: '#8E5B35' },

  // ---- gems: one cut, each stone's own colours (Sam, 9 Oct)
  diamond: gem('#D7E9F8', '#9FBEDD', [[0, '#FFFFFF'], [0.5, '#DDEEFB'], [1, '#A9C7E6']], {
    w: ['#FFFFFF', 0.85], i: ['#E3F1FC', 0.65], b: ['#ADCDEE', 0.65], s: ['#7FA3CC', 0.55],
    l: ['#E6DCFF', 0.6], m: ['#D2F3F2', 0.55], p: ['#FFE3F1', 0.55],
  }),
  ruby: gem('#E0245E', '#7A0626', [[0, '#FF8FB1'], [0.5, '#E0245E'], [1, '#7A0626']], {
    w: ['#FFE3EC', 0.75], i: ['#FF7AA2', 0.65], b: ['#D0104C', 0.6], s: ['#6E0322', 0.6],
    l: ['#FF4F8B', 0.55], m: ['#FFB3C9', 0.55], p: ['#FFD27A', 0.4],
  }),
  emerald: gem('#1FAE64', '#04502A', [[0, '#9CF2C4'], [0.5, '#1FAE64'], [1, '#04502A']], {
    w: ['#E6FFF1', 0.75], i: ['#6FE3A3', 0.65], b: ['#139E58', 0.6], s: ['#033D20', 0.6],
    l: ['#4ED39A', 0.55], m: ['#B8F5D6', 0.55], p: ['#E8FFB0', 0.4],
  }),
  sapphire: gem('#2A5BD7', '#0B1F6B', [[0, '#9DBBFF'], [0.5, '#2A5BD7'], [1, '#0B1F6B']], {
    w: ['#E6EEFF', 0.75], i: ['#7FA2FF', 0.65], b: ['#1F4FD0', 0.6], s: ['#081A5C', 0.6],
    l: ['#5C7CFF', 0.55], m: ['#B9CCFF', 0.55], p: ['#C9B3FF', 0.45],
  }),
  amethyst: gem('#9B59D9', '#4A1A7A', [[0, '#E3C6FF'], [0.5, '#9B59D9'], [1, '#4A1A7A']], {
    w: ['#F6EBFF', 0.75], i: ['#C79BF2', 0.65], b: ['#8E44C9', 0.6], s: ['#3D1066', 0.6],
    l: ['#B37BEB', 0.55], m: ['#E2CCFA', 0.55], p: ['#FFC9EA', 0.45],
  }),
  topaz: gem('#F5A623', '#9A5A06', [[0, '#FFE7A8'], [0.5, '#F5A623'], [1, '#9A5A06']], {
    w: ['#FFF8E0', 0.8], i: ['#FFD27A', 0.65], b: ['#E8901A', 0.6], s: ['#8A4B05', 0.55],
    l: ['#FFC04D', 0.55], m: ['#FFE9B8', 0.55], p: ['#FFB0A0', 0.4],
  }),

  // ---- metals (Sam, 9 Oct: the old gold "does not have the metallic feel")
  gold: metal('#E8B82A', '#6B4204', [
    [0, '#FFF7D6'], [0.14, '#FFE07A'], [0.28, '#D9A21B'], [0.4, '#8A5A06'], [0.47, '#B07A0C'],
    [0.53, '#FFE9A0'], [0.6, '#F2C230'], [0.78, '#A8700A'], [0.92, '#6B4204'], [1, '#C9921E'],
  ]),
  titanium: metal('#9AA5B1', '#3E4852', [
    [0, '#FFFFFF'], [0.14, '#DDE3E9'], [0.28, '#9AA5B1'], [0.4, '#5C6773'], [0.47, '#7D8995'],
    [0.53, '#F2F5F8'], [0.6, '#B9C3CC'], [0.78, '#6E7A86'], [0.92, '#3E4852'], [1, '#8E9AA6'],
  ], true),
  bronze: metal('#C47A3A', '#4A2610', [
    [0, '#FFE6C7'], [0.14, '#F0B27A'], [0.28, '#C47A3A'], [0.4, '#6E3A14'], [0.47, '#8A4E22'],
    [0.53, '#FFD2A0'], [0.6, '#D8925A'], [0.78, '#8A4E22'], [0.92, '#4A2610'], [1, '#B06A30'],
  ]),

  // ---- space
  galaxy: {
    limbs: '#5B3FA8', shade: '#24124F', face: '#FFF6C2',
    stops: [[0, '#7B5BD6'], [0.5, '#3B2384'], [1, '#160A36']],
    css: 'radial-gradient(circle at 40% 35%,#7B5BD6,#3B2384 55%,#160A36)',
    finish: () => (
      <g fill="#FFFFFF">
        {[[44, 70, 1.6], [72, 62, 1.2], [82, 96, 1.8], [38, 112, 1.3], [66, 126, 1.5], [52, 90, 0.9], [78, 120, 1]].map(([x, y, r], i) => (
          <circle key={i} className="ws-buddy-twinkle" cx={x} cy={y} r={r} style={{ animationDelay: `${i * 0.3}s` }} />
        ))}
        {sparkle(60, 50, 4, 's1')}
      </g>
    ),
  },
  // Clouds of glowing gas in many colours, where galaxy is one purple.
  nebula: {
    limbs: '#7E3FB0', shade: '#160A36', face: '#FFF1F8', radial: true,
    stops: [[0, '#2A1250'], [0.7, '#160A36'], [1, '#0A0520']],
    css: 'radial-gradient(circle at 30% 35%,#FF5FA2 0 14%,transparent 34%),radial-gradient(circle at 70% 70%,#3FE0D0 0 12%,transparent 32%),radial-gradient(circle at 65% 30%,#9B5BFF 0 10%,transparent 30%),#160A36',
    defs: (uid) => (
      <>
        {([['n1', '#FF5FA2'], ['n2', '#3FE0D0'], ['n3', '#9B5BFF'], ['n4', '#FFB35C']] as const).map(([id, c]) => (
          <radialGradient key={id} id={`${id}${uid}`}>
            <stop offset="0" stopColor={c} stopOpacity="0.9" />
            <stop offset="1" stopColor={c} stopOpacity="0" />
          </radialGradient>
        ))}
      </>
    ),
    finish: (uid) => (
      <g>
        {NEBULA_CLOUDS.map(([g, cx, cy, rx, ry, a], i) => (
          <ellipse key={i} cx={cx} cy={cy} rx={rx} ry={ry} transform={`rotate(${a} ${cx} ${cy})`} fill={`url(#${g}${uid})`} />
        ))}
        <g fill="#FFFFFF">
          {[[40, 58, 1], [84, 78, 1.2], [30, 96, 0.8], [88, 120, 0.9], [54, 134, 1], [68, 44, 0.8]].map(([x, y, r], i) => (
            <circle key={i} className="ws-buddy-twinkle" cx={x} cy={y} r={r} style={{ animationDelay: `${i * 0.35}s` }} />
          ))}
        </g>
        {sparkle(80, 66, 4, 'n')}
      </g>
    ),
  },
  // A star blowing apart: a white-hot middle and a burst of rays behind.
  supernova: {
    limbs: '#FF8A3C', shade: '#B8326B', radial: true,
    stops: [[0, '#FFFFFF'], [0.28, '#FFF3A0'], [0.52, '#FFB347'], [0.76, '#FF4F8B'], [1, '#6A1E9A']],
    css: 'radial-gradient(circle,#FFFFFF 0 12%,#FFF3A0 22%,#FFB347 38%,#FF4F8B 58%,#6A1E9A 80%)',
    defs: (uid) => (
      <radialGradient id={`sn${uid}`} cx="60" cy="94" r="72" gradientUnits="userSpaceOnUse">
        <stop offset="0.55" stopColor="#FFE27A" stopOpacity="0.95" />
        <stop offset="0.8" stopColor="#FF6A3C" stopOpacity="0.7" />
        <stop offset="1" stopColor="#FF4FA0" stopOpacity="0" />
      </radialGradient>
    ),
    behind: (uid) => (
      <g className="ws-buddy-pulse">
        <polygon points={burst} fill={`url(#sn${uid})`} />
      </g>
    ),
    finish: () => (
      <g>
        <ellipse cx={60} cy={96} rx={30} ry={34} fill="none" stroke="#FFFFFF" strokeWidth={1.5} opacity={0.5} />
        {sparkle(44, 62, 4, 'a')}{sparkle(82, 116, 3, 'b')}{sparkle(76, 56, 2.5, 'c')}
      </g>
    ),
  },
  // Sam, 9 Oct: "a black hole". Black at the heart, an orange edge where
  // light bends round it, and a ring of fire (the accretion disc).
  blackhole: {
    limbs: '#3A2654', shade: '#000000', face: '#FFE2B0', radial: true,
    stops: [[0, '#000000'], [0.6, '#06030C'], [0.85, '#1B0B24'], [1, '#4A1C0A']],
    css: 'radial-gradient(circle at 50% 50%,#000 0 36%,#FFE2A8 40%,#FF8A1F 46%,#7A2A0C 54%,#12081C 68%,#05030A)',
    defs: (uid) => (
      <>
        <radialGradient id={`h${uid}`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0.55" stopColor="#FF8A1F" stopOpacity="0.55" />
          <stop offset="1" stopColor="#FF8A1F" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`r${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#B33A00" />
          <stop offset="0.3" stopColor="#FF8A1F" />
          <stop offset="0.5" stopColor="#FFE9B8" />
          <stop offset="0.7" stopColor="#FF8A1F" />
          <stop offset="1" stopColor="#B33A00" />
        </linearGradient>
      </>
    ),
    behind: (uid) => (
      <g>
        <ellipse cx={60} cy={92} rx={52} ry={62} fill={`url(#h${uid})`} opacity={0.8} />
        <g transform="translate(60 121) rotate(-8)">
          <path d={RING_BACK} fill="none" stroke="#FF8A1F" strokeWidth={8} opacity={0.25} strokeLinecap="round" />
          <path d={RING_BACK} fill="none" stroke={`url(#r${uid})`} strokeWidth={4} strokeLinecap="round" />
        </g>
      </g>
    ),
    finish: () => (
      <g>
        <path d={BODY} fill="none" stroke="#FF8A1F" strokeWidth={8} opacity={0.45} />
        <path d={BODY} fill="none" stroke="#FFE2A8" strokeWidth={2.4} opacity={0.55} />
        <g fill="#FFFFFF" opacity={0.7}>
          {[[44, 66, 0.9], [74, 70, 0.7], [36, 100, 0.6], [86, 104, 0.8]].map(([x, y, r], i) => (
            <circle key={i} className="ws-buddy-twinkle" cx={x} cy={y} r={r} style={{ animationDelay: `${i * 0.4}s` }} />
          ))}
        </g>
      </g>
    ),
    front: (uid) => (
      <g transform="translate(60 121) rotate(-8)">
        <path d={RING_FRONT} fill="none" stroke="#FF8A1F" strokeWidth={8} opacity={0.3} strokeLinecap="round" />
        <path d={RING_FRONT} fill="none" stroke={`url(#r${uid})`} strokeWidth={5.5} strokeLinecap="round" />
        <path d={RING_FRONT} fill="none" stroke="#FFF4D6" strokeWidth={1.1} opacity={0.9} strokeLinecap="round" />
      </g>
    ),
  },
  // Sam, 11 Oct: "a portal like Dr. Strange". The body is the place on the
  // other side, dark with stars, and a ring of orange sparks runs round it,
  // and round the buddy's edge, in opposite directions.
  portal: {
    limbs: '#F08A24', shade: '#0C0826', face: '#FFE9B8', radial: true,
    stops: [[0, '#3A2A7A'], [0.6, '#1E1450'], [1, '#0C0826']],
    css: 'radial-gradient(circle,#1E1450 0 40%,#FFF1C2 45%,#FF9A1F 51%,transparent 57%),repeating-conic-gradient(#FF9A1F 0 8deg,#2A1650 8deg 20deg)',
    behind: () => (
      <g transform="translate(60 92) scale(1 1.24)" fill="none">
        <circle r={47} stroke="#FF8A1F" strokeWidth={11} opacity={0.3} />
        <circle className="ws-buddy-orbit" r={47} stroke="#FFB347" strokeWidth={4} strokeDasharray="6 3" />
        <circle className="ws-buddy-orbit-back" r={43} stroke="#FFF1C2" strokeWidth={1.4} strokeDasharray="2 7" />
      </g>
    ),
    finish: () => (
      <g fill="none">
        <path d="M60 70 C80 72 84 96 66 104 C52 110 42 96 50 86 C56 80 66 84 64 92" stroke="#7B6BD6" strokeWidth={2} opacity={0.5} strokeLinecap="round" />
        <g fill="#FFFFFF">
          {[[44, 64, 1], [78, 76, 1.2], [36, 106, 0.8], [84, 114, 1], [58, 128, 0.9]].map(([x, y, r], i) => (
            <circle key={i} className="ws-buddy-twinkle" cx={x} cy={y} r={r} style={{ animationDelay: `${i * 0.35}s` }} />
          ))}
        </g>
        <path d={BODY} stroke="#FF8A1F" strokeWidth={9} opacity={0.45} />
        <path className="ws-buddy-orbit" d={BODY} stroke="#FFB347" strokeWidth={3} strokeDasharray="5 4" />
        <path className="ws-buddy-orbit-back" d={BODY} stroke="#FFF4D6" strokeWidth={1.2} strokeDasharray="2 7" />
      </g>
    ),
    front: () => (
      <g>
        {sparkle(12, 76, 3.5, 'p1', '#FFC04D')}{sparkle(108, 70, 3, 'p2', '#FFC04D')}
        {sparkle(8, 112, 2.5, 'p3', '#FFE2A0')}{sparkle(112, 116, 3.5, 'p4', '#FFC04D')}
        {sparkle(28, 30, 2.5, 'p5', '#FFE2A0')}{sparkle(94, 26, 3, 'p6', '#FFC04D')}
      </g>
    ),
  },

  // ---- nature
  wood: {
    limbs: '#B5773F', shade: '#7A4A22',
    stops: [[0, '#E2AA70'], [0.5, '#C68A52'], [1, '#9A6232']],
    css: 'repeating-linear-gradient(95deg,#C68A52 0 5px,#B5773F 5px 7px,#D49A60 7px 11px,#A86A35 11px 12px)',
    finish: () => (
      <g fill="none" stroke="#7A4A22" strokeLinecap="round">
        {WOOD_GRAIN.map((d, i) => <path key={i} d={d} strokeWidth={i % 2 ? 1 : 1.6} opacity={0.5} />)}
        {/* a knot, with the grain bending round it */}
        <ellipse cx={76} cy={106} rx={7} ry={4.5} fill="#A86A35" strokeWidth={1.6} opacity={0.9} />
        <ellipse cx={76} cy={106} rx={3.5} ry={2} fill="#7A4A22" strokeWidth={1} />
        <ellipse cx={76} cy={106} rx={11} ry={7.5} strokeWidth={1.2} opacity={0.55} />
        <path d="M52 48 C44 62 38 78 36 96" stroke="#FFFFFF" strokeWidth={3} opacity={0.25} />
      </g>
    ),
  },
  ice: {
    limbs: '#BDEBFA', shade: '#6CBEE0', tilt: 1,
    stops: [[0, '#FFFFFF'], [0.4, '#D8F4FC'], [1, '#7FCBE8']],
    css: 'linear-gradient(135deg,#FFFFFF 0 22%,#D8F4FC 22% 46%,#FFFFFF 46% 49%,#BDEBFA 49% 74%,#8FD3EE 74%)',
    finish: () => (
      <g>
        {/* frost: a snowy cap and fine cracks */}
        <path d="M44 56 C50 46 70 46 76 56 C72 54 68 58 64 55 C60 59 56 55 52 58 C49 55 46 58 44 56 Z" fill="#FFFFFF" opacity={0.95} />
        <path d="M30 84 L38 90 L36 98 M38 90 L44 88 M86 112 L80 118 L82 126 M80 118 L74 117" stroke="#FFFFFF" strokeWidth={1.2} fill="none" opacity={0.85} />
        <path d="M52 62 C42 74 36 88 35 102" stroke="#FFFFFF" strokeWidth={4} fill="none" strokeLinecap="round" opacity={0.7} />
        {sparkle(82, 76, 4, 'i1')}{sparkle(40, 120, 3, 'i2')}
      </g>
    ),
    front: () => (
      <g>
        {[['M53 135 L60 149 L67 135 Z', 'M53.5 137 L60 149 L66.5 137'],
          ['M24 112 L25 128 L31 121 Z', 'M24.3 114 L25 128 L30.4 122.5'],
          ['M28 120 L30 134 L36 127 Z', 'M28.4 121.5 L30 134 L35.2 128.5'],
          ['M96 112 L95 128 L89 121 Z', 'M95.7 114 L95 128 L89.6 122.5'],
          ['M92 120 L90 134 L84 127 Z', 'M91.6 121.5 L90 134 L84.8 128.5']].map(([fill, edge], i) => (
          <g key={i}>
            <path d={fill} fill="#CFF1FB" />
            <path d={edge} fill="none" stroke={INK} strokeWidth={2.2} strokeLinejoin="round" />
          </g>
        ))}
      </g>
    ),
  },
  lava: {
    limbs: '#4A2418', shade: '#1A0A06', face: '#FFE2B0', radial: true,
    stops: [[0, '#5A2A1A'], [0.6, '#3A1A10'], [1, '#1E0C08']],
    css: 'radial-gradient(circle at 50% 55%,#FFE08A 0 5%,#FF8A1F 12%,#C2360C 22%,#3A1A10 36%,#1E0C08)',
    finish: () => (
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* a glow up from below, then the cracks: wide and dim, narrow and bright, hot white */}
        <ellipse cx={60} cy={146} rx={44} ry={22} fill="#FF5A00" opacity={0.35} />
        {LAVA_CRACKS.map((d, i) => <path key={`a${i}`} d={d} stroke="#FF5A00" strokeWidth={5} opacity={0.4} />)}
        {LAVA_CRACKS.map((d, i) => <path key={`b${i}`} d={d} stroke="#FFA030" strokeWidth={2} />)}
        {LAVA_CRACKS.map((d, i) => <path key={`c${i}`} d={d} stroke="#FFF1B0" strokeWidth={0.7} />)}
        <g fill="#FFC04D">
          {[[48, 60, 1.2], [76, 70, 1], [86, 136, 1.3], [32, 130, 1]].map(([x, y, r], i) => (
            <circle key={i} className="ws-buddy-twinkle" cx={x} cy={y} r={r} style={{ animationDelay: `${i * 0.5}s` }} />
          ))}
        </g>
      </g>
    ),
  },
  // Sam, 11 Oct. Lava is rock with fire inside; this is fire itself: red at
  // the top, yellow-hot at the bottom, flames licking off every edge.
  fire: {
    limbs: '#FF6A1A', shade: '#B3220E',
    stops: [[0, '#D62A12'], [0.45, '#FF6A1A'], [0.8, '#FFA726'], [1, '#FFD45A']],
    css: 'radial-gradient(ellipse at 50% 90%,#FFF3B0 0 12%,#FFC02E 28%,#FF7A1A 52%,#E8341C 75%,#9E1A0E)',
    behind: () => (
      <g>
        {FLAMES.map(([x, y, w, h, a], i) => (
          <g key={i} transform={`translate(${x} ${y}) rotate(${a})`}>
            <path className="ws-buddy-flicker" d={flame(w, h)} fill="#FF7A1A" style={{ animationDelay: `${i * 0.15}s` }} />
            <path className="ws-buddy-flicker" d={flame(w * 0.55, h * 0.6)} fill="#FFD45A" style={{ animationDelay: `${i * 0.15 + 0.3}s` }} />
          </g>
        ))}
        <g fill="#FFC04D">
          {[[16, 60, 1.6], [106, 66, 1.4], [10, 100, 1.2], [110, 104, 1.5], [30, 30, 1.2], [92, 34, 1.3]].map(([x, y, r], i) => (
            <circle key={i} className="ws-buddy-twinkle" cx={x} cy={y} r={r} style={{ animationDelay: `${i * 0.3}s` }} />
          ))}
        </g>
      </g>
    ),
    finish: () => (
      <g>
        <g transform="translate(40 142)"><path className="ws-buddy-flicker" d={flame(14, 36)} fill="#FFB02E" /></g>
        <g transform="translate(80 142)"><path className="ws-buddy-flicker" d={flame(14, 36)} fill="#FFB02E" style={{ animationDelay: '0.4s' }} /></g>
        <g transform="translate(60 144)"><path className="ws-buddy-flicker" d={flame(26, 62)} fill="#FFD45A" opacity={0.9} style={{ animationDelay: '0.2s' }} /></g>
        <g transform="translate(60 144)"><path className="ws-buddy-flicker" d={flame(14, 34)} fill="#FFF6D0" style={{ animationDelay: '0.5s' }} /></g>
      </g>
    ),
  },
  // Sam, 11 Oct: "root (like Groot)". A tree that walks: twisted bark, moss,
  // a vine, leaves sprouting at the shoulders and roots at the feet.
  root: {
    limbs: '#8C603A', shade: '#5A3A1E', tilt: 0.3,
    stops: [[0, '#B8885A'], [0.55, '#9A6B42'], [1, '#734A2A']],
    css: 'radial-gradient(circle at 30% 26%,#5DBB4A 0 14%,transparent 15%),repeating-linear-gradient(88deg,#9A6B42 0 4px,#6B4526 4px 5px,#B8885A 5px 8px,#7E5432 8px 9px)',
    finish: () => (
      <g fill="none" strokeLinecap="round">
        {BARK.map((d, i) => <path key={`d${i}`} d={d} stroke="#5A3A1E" strokeWidth={i % 2 ? 1.4 : 2.2} opacity={0.55} />)}
        {BARK.map((d, i) => <path key={`l${i}`} d={d} transform="translate(1.8 0)" stroke="#D2A676" strokeWidth={0.9} opacity={0.45} />)}
        <ellipse cx={36} cy={64} rx={7} ry={4} fill="#6BAA3E" opacity={0.85} />
        <ellipse cx={84} cy={120} rx={8} ry={4} fill="#6BAA3E" opacity={0.85} />
        <path d="M22 106 C40 98 50 114 66 106 S 90 94 100 100" stroke="#4E9A3A" strokeWidth={2.4} />
        {[[38, 101, -30], [62, 107, 30], [86, 97, -20]].map(([x, y, a], i) => (
          <ellipse key={i} cx={x} cy={y} rx={3.5} ry={1.8} transform={`rotate(${a} ${x} ${y})`} fill="#7CCB5A" />
        ))}
      </g>
    ),
    front: () => (
      <g strokeLinecap="round" strokeLinejoin="round">
        {ROOTS.map((d, i) => <path key={`o${i}`} d={d} fill="none" stroke={INK} strokeWidth={i % 2 ? 4.4 : 5.6} />)}
        {ROOTS.map((d, i) => <path key={`f${i}`} d={d} fill="none" stroke="#8C603A" strokeWidth={i % 2 ? 2 : 3.2} />)}
        {/* a sprig at each shoulder */}
        {([[30, 74, 20, 60], [90, 72, 100, 58]] as const).map(([x1, y1, x2, y2], i) => (
          <g key={i}>
            <path d={`M${x1} ${y1} L${x2} ${y2}`} stroke={INK} strokeWidth={2.2} />
            {[[x2, y2, i ? 30 : -30], [(x1 + x2) / 2, (y1 + y2) / 2, i ? -40 : 40]].map(([x, y, a], j) => (
              <ellipse key={j} cx={x} cy={y} rx={5} ry={2.6} transform={`rotate(${a} ${x} ${y})`}
                fill="#5DBB4A" stroke={INK} strokeWidth={1.5} />
            ))}
          </g>
        ))}
      </g>
    ),
  },

  // ---- fun
  rainbow: {
    limbs: '#F57C1F', shade: '#7A5AC4',
    stops: [[0, '#E2557A'], [0.2, '#F57C1F'], [0.4, '#F6C343'], [0.6, '#4CC3A0'], [0.8, '#5AB4E5'], [1, '#9B7BE0']],
    css: 'linear-gradient(180deg,#E2557A,#F57C1F,#F6C343,#4CC3A0,#5AB4E5,#9B7BE0)',
  },
  candy: {
    limbs: '#F48FB1', shade: '#D25887',
    stops: [[0, '#FFC1D9'], [1, '#F48FB1']],
    css: 'repeating-linear-gradient(135deg,#F48FB1 0 8px,#FFFFFF 8px 14px)',
    defs: (uid) => (
      <pattern id={`p${uid}`} width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(40)">
        <rect width="6" height="12" fill="#FFFFFF" opacity="0.75" />
      </pattern>
    ),
    finish: (uid) => <rect x={20} y={30} width={80} height={112} fill={`url(#p${uid})`} />,
  },
  // Slime is told by what it does: it drips, it puddles, it has bubbles in
  // it and a wet shine (Sam: "not sure how to render it to communicate it's
  // slime").
  slime: {
    limbs: '#5FD43F', shade: '#1F8E2A',
    stops: [[0, '#D2FF8A'], [0.45, '#6BE04A'], [1, '#1F9E2A']],
    css: 'radial-gradient(circle at 35% 30%,#F0FFD0 0 10%,#9CF060 22%,#4CC63A 60%,#1F8E2A)',
    behind: () => <ellipse cx={60} cy={151} rx={26} ry={4.5} fill="#5FD43F" stroke={INK} strokeWidth={2.2} />,
    finish: () => (
      <g>
        {[[44, 74, 4], [78, 68, 3], [70, 122, 5], [40, 114, 3], [86, 98, 2.5], [56, 132, 2]].map(([x, y, r], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r={r} fill="#E8FFD0" opacity={0.45} stroke="#FFFFFF" strokeWidth={0.8} />
            <circle cx={x! - r! * 0.35} cy={y! - r! * 0.35} r={r! * 0.3} fill="#FFFFFF" opacity={0.9} />
          </g>
        ))}
        {/* a coat of brighter goo running down the head */}
        <path d="M16 30 L104 30 L104 62 C98 62 97 76 92 76 C87 76 88 64 84 64 C80 64 80 84 74 84 C68 84 70 66 64 66 C60 66 61 74 56 74 C51 74 52 62 47 62 C42 62 43 80 37 80 C31 80 33 64 26 64 C21 64 20 70 16 70 Z"
          fill="#B8FF6A" opacity={0.9} />
        <path d="M104 62 C98 62 97 76 92 76 C87 76 88 64 84 64 C80 64 80 84 74 84 C68 84 70 66 64 66 C60 66 61 74 56 74 C51 74 52 62 47 62 C42 62 43 80 37 80 C31 80 33 64 26 64 C21 64 20 70 16 70"
          fill="none" stroke="#2E9E2A" strokeWidth={1.6} />
        <path d="M46 50 C37 61 32 74 33 86 C39 74 46 63 53 54 Z" fill="#FFFFFF" opacity={0.6} />
      </g>
    ),
    // All three drips hang from the underside. Until 22 Oct 2026 two ran
    // down the sides, where the arms are, and read as a second pair of arms
    // (Sam: "slime version looks like it has 4 arms").
    front: () => (
      <g>
        {[['M53 135 C55 141 54 149 60 150 C66 149 65 141 67 135 Z', 'M54 137 C55 141 54 149 60 150 C66 149 65 141 66 137'],
          ['M38 131 C39 136 38 143 42 144 C46 143 45 136 47 133 Z', 'M38.6 133 C39 137 38 143 42 144 C46 143 45 137 46.4 134.5'],
          ['M73 133 C75 136 74 142 78 143 C82 142 81 136 82 131 Z', 'M73.6 134.5 C75 137 74 142 78 143 C82 142 81 137 81.4 133']].map(([fill, edge], i) => (
          <g key={i}>
            <path d={fill} fill="#5FD43F" />
            <path d={edge} fill="none" stroke={INK} strokeWidth={2.4} strokeLinecap="round" />
          </g>
        ))}
        <circle cx={58} cy={146} r={1.6} fill="#FFFFFF" opacity={0.9} />
        <circle cx={41} cy={141} r={1.3} fill="#FFFFFF" opacity={0.9} />
        <circle cx={77} cy={140} r={1.3} fill="#FFFFFF" opacity={0.9} />
      </g>
    ),
  },
  camo: {
    limbs: '#6B7A3A', shade: '#3F4A22',
    stops: [[0, '#7E8E48'], [1, '#5E6E35']],
    css: 'radial-gradient(circle at 28% 30%,#3F4A22 0 17%,transparent 18%),radial-gradient(circle at 70% 66%,#A8955E 0 19%,transparent 20%),radial-gradient(circle at 74% 24%,#2E2A1E 0 11%,transparent 12%),radial-gradient(circle at 30% 76%,#A8955E 0 12%,transparent 13%),#6E7E40',
    finish: () => <g>{CAMO.map(([c, d], i) => <path key={i} d={d} fill={c} opacity={0.9} />)}</g>,
  },
} satisfies Record<string, Material>

export type BodyColor = keyof typeof MATERIALS

export function material(color: string): Material {
  return (MATERIALS as Record<string, Material>)[color] ?? MATERIALS.tangerine
}

/** How the buddy maker groups the colours. */
export const COLOR_GROUPS: [string, BodyColor[]][] = [
  ['Everyday', ['tangerine', 'peach', 'berry', 'sunny', 'sky', 'mint', 'grape', 'cocoa']],
  ['Gems', ['diamond', 'ruby', 'emerald', 'sapphire', 'amethyst', 'topaz']],
  ['Metals', ['gold', 'titanium', 'bronze']],
  ['Space', ['galaxy', 'nebula', 'supernova', 'blackhole', 'portal']],
  ['Nature', ['wood', 'ice', 'lava', 'fire', 'root']],
  ['Fun', ['rainbow', 'candy', 'slime', 'camo']],
]

export const COLOR_LABELS: Record<BodyColor, string> = {
  tangerine: 'Tangerine', peach: 'Peach', berry: 'Berry', sunny: 'Sunny', sky: 'Sky',
  mint: 'Mint', grape: 'Grape', cocoa: 'Cocoa',
  diamond: 'Diamond', ruby: 'Ruby', emerald: 'Emerald', sapphire: 'Sapphire', amethyst: 'Amethyst', topaz: 'Topaz',
  gold: 'Gold', titanium: 'Titanium', bronze: 'Bronze',
  galaxy: 'Galaxy', nebula: 'Nebula', supernova: 'Supernova', blackhole: 'Black hole', portal: 'Portal',
  wood: 'Wood', ice: 'Ice', lava: 'Lava', fire: 'Fire', root: 'Root',
  rainbow: 'Rainbow', candy: 'Candy', slime: 'Slime', camo: 'Camo',
}
